/**
 * Field validation, in one place.
 *
 * The rules were previously spread across the surfaces that happened to care:
 * a script check living inside `TextInput`, a second copy of the Arabic and
 * Latin regexes in the onboarding page, and nothing at all on the till's
 * customer form or the add-on editor — where a cashier could save a customer
 * with a name of "٣" and a phone of "asdf", and a manager could save an add-on
 * group whose Arabic name was typed in English.
 *
 * These functions return an error MESSAGE or null. They do not render, they do
 * not throw, and they do not know about React — so the same rule can be used by
 * a controlled input, a submit handler, or a test.
 *
 * ── On script validation ─────────────────────────────────────────────────────
 * This product is bilingual, and the two name fields are not interchangeable:
 * `name_ar` is what prints on a KSA tax invoice and what an Arabic-reading
 * customer sees. English typed into it is not a style choice, it is a wrong
 * value that nothing downstream can detect. So an `ar` field must contain
 * Arabic and an `en` field must not — checked as "contains a character of the
 * wrong script". Digits and punctuation pass in either field ("لاتيه 250"),
 * but the two alphabets do not mix: a Latin brand name inside an Arabic name
 * used to be tolerated, and what that tolerance actually let through was
 * half-translated names ("Latte لاتيه") that then printed as-is on invoices.
 * Each field takes one alphabet; the other alphabet has its own field.
 */

/** Arabic, including the presentation forms and supplements. */
export const ARABIC_RE = /[؀-ۿݐ-ݿࢠ-ࣿﭐ-﷿ﹰ-﻿]/
/** Latin letters. */
export const LATIN_RE = /[A-Za-z]/

export type FieldScript = 'ar' | 'en' | 'any'

/** A value made only of digits, which is never a name. */
const PURE_DIGITS_RE = /^[\d\s٠-٩]+$/

export function requiredError(value: string, label = 'This field'): string | null {
  return value.trim() === '' ? `${label} is required.` : null
}

export function maxLengthError(value: string, max: number): string | null {
  return value.length > max ? `Use ${max} characters or fewer.` : null
}

/**
 * Wrong-script detection. Empty is not an error here — use `requiredError` for
 * that, so an optional Arabic field left blank stays valid.
 */
export function scriptError(value: string, script: FieldScript): string | null {
  const trimmed = value.trim()
  if (!trimmed || script === 'any') return null
  if (script === 'en' && ARABIC_RE.test(trimmed)) {
    return 'Use English here — the Arabic name has its own field.'
  }
  if (script === 'ar' && !ARABIC_RE.test(trimmed)) {
    return 'Use Arabic here — the English name has its own field.'
  }
  if (script === 'ar' && LATIN_RE.test(trimmed)) {
    return 'Arabic only here — no English letters mixed in.'
  }
  return null
}

/** A name that is only digits is a data-entry slip, in either script. */
export function nameError(value: string, script: FieldScript, opts: {
  required?: boolean; max?: number; label?: string
} = {}): string | null {
  const { required = false, max, label = 'Name' } = opts
  if (required) {
    const missing = requiredError(value, label)
    if (missing) return missing
  }
  const trimmed = value.trim()
  if (!trimmed) return null
  if (max) {
    const tooLong = maxLengthError(value, max)
    if (tooLong) return tooLong
  }
  if (PURE_DIGITS_RE.test(trimmed)) return 'A name cannot be only numbers.'
  return scriptError(value, script)
}

/**
 * Saudi-first phone validation.
 *
 * Accepts the three forms staff actually type — 05XXXXXXXX, 5XXXXXXXX and
 * +9665XXXXXXXX — plus any other E.164 international number, because a KSA
 * business does serve foreign customers and refusing their number outright is
 * worse than accepting it. Arabic-Indic digits are normalised first: a phone
 * typed on an Arabic keypad is the same phone.
 */
export function normalizePhone(value: string): string {
  const arabicIndic = '٠١٢٣٤٥٦٧٨٩'
  return value
    .replace(/[٠-٩]/g, d => String(arabicIndic.indexOf(d)))
    .replace(/[\s()\-.]/g, '')
}

export function phoneError(value: string, opts: { required?: boolean } = {}): string | null {
  const raw = value.trim()
  if (!raw) return opts.required ? 'A phone number is required.' : null
  const phone = normalizePhone(raw)
  if (!/^\+?\d+$/.test(phone)) return 'A phone number can only contain digits.'
  if (/^(?:\+966|00966)5\d{8}$/.test(phone)) return null
  if (/^05\d{8}$/.test(phone)) return null
  if (/^5\d{8}$/.test(phone)) return null
  // Any other country: E.164 allows up to 15 digits.
  if (/^\+\d{8,15}$/.test(phone)) return null
  return 'Enter a Saudi mobile (05XXXXXXXX) or an international number with its country code.'
}

/** Canonical +9665XXXXXXXX for a Saudi number; other input is returned as-is. */
export function canonicalSaudiPhone(value: string): string {
  const phone = normalizePhone(value)
  if (/^05\d{8}$/.test(phone)) return `+966${phone.slice(1)}`
  if (/^5\d{8}$/.test(phone)) return `+966${phone}`
  if (/^009665\d{8}$/.test(phone)) return `+${phone.slice(2)}`
  return phone
}

export function emailError(value: string, opts: { required?: boolean } = {}): string | null {
  const trimmed = value.trim()
  if (!trimmed) return opts.required ? 'An email address is required.' : null
  // Deliberately permissive: the authority on whether an address exists is a
  // message sent to it, and an over-strict pattern rejects valid addresses.
  if (!/^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/.test(trimmed)) return 'Enter a valid email address.'
  if (ARABIC_RE.test(trimmed)) return 'An email address cannot contain Arabic characters.'
  return null
}

/** A money or count field: a number, not negative, within an optional cap. */
export function numberError(value: string, opts: {
  required?: boolean; min?: number; max?: number; label?: string; integer?: boolean
} = {}): string | null {
  const { required = false, min = 0, max, label = 'This value', integer = false } = opts
  const trimmed = value.trim()
  if (!trimmed) return required ? `${label} is required.` : null
  const n = Number(normalizePhone(trimmed))
  if (!Number.isFinite(n)) return `${label} must be a number.`
  if (integer && !Number.isInteger(n)) return `${label} must be a whole number.`
  if (n < min) return `${label} cannot be less than ${min}.`
  if (max != null && n > max) return `${label} cannot be more than ${max}.`
  return null
}

/**
 * A vehicle registration plate.
 *
 * Deliberately loose on FORM and strict only on characters. A Saudi plate is
 * three letters and up to four digits, shown in both Arabic and Latin — but the
 * field also has to hold a GCC plate, a diplomatic plate, a temporary paper
 * plate and whatever a customer reads off the back of a car they borrowed. The
 * useful guard is against someone typing a phone number or a name into it, not
 * against an unfamiliar but real plate.
 *
 * Optional everywhere it appears: a customer who never orders to their car
 * should not be asked for one.
 */
export function plateError(value: string, opts: { required?: boolean } = {}): string | null {
  const trimmed = value.trim()
  if (!trimmed) return opts.required ? 'A plate number is required.' : null
  if (trimmed.length > 20) return 'Use 20 characters or fewer.'
  // Latin letters, Arabic letters, Western and Arabic-Indic digits, and the
  // spaces or hyphens people separate the two halves with.
  if (!/^[A-Za-z؀-ۿ0-9٠-٩\s-]+$/.test(trimmed)) {
    return 'A plate can only contain letters, numbers, spaces and hyphens.'
  }
  if (!/[0-9٠-٩]/.test(trimmed) && !/[A-Za-z؀-ۿ]/.test(trimmed)) {
    return 'That does not look like a plate number.'
  }
  return null
}

/** First non-null error, so a field can carry several rules. */
export function firstError(...errors: (string | null)[]): string | null {
  return errors.find(e => e != null) ?? null
}
